// Shared
import { getTextWidth, formatCurrency } from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';
import AddNew from '../components/detail/AddNew/AddNew';

// Taxonomies
import { yearCore, yearFilter } from '../taxonomies/year';
import { countryCore, countryFilter } from '../taxonomies/country';
import { modelCore, modelFilter, modelDetail } from '../taxonomies/model';
import { engineCore, engineFilter, engineDetail } from '../taxonomies/engine';
import {
  salesVersionCore,
  salesVersionFilter,
  salesVersionDetail
} from '../taxonomies/salesVersion';
import {
  gearboxCore,
  gearboxFilter,
  gearboxDetail
} from '../taxonomies/gearbox';
import { statusCore, statusFilter } from '../taxonomies/status';


// Trim core
export function trimCore() {
  const endpoint = '/trims';
  const endpointDescriptionVersion = '/descriptionversion/type/trim/id';
  const endpointPriceVersion = '/priceversion/type/trim/id';
  const detailId = 'trimId';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  return {
    endpoint,
    endpointDescriptionVersion,
    endpointPriceVersion,
    detailId,
    getDetailId: getDetailId
  };
}

// Trim filter
export function trimFilter() {
  const parameter = 'TrimId';

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        value: row.trimId,
        code: row.trimCode
      };

      // Object values
      const objects = {
        label: row.trimDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  return {
    parameter,
    getRows: getRows
  };
}

// Trim listing
export function trimListing() {

  // Initial sort
  const initialSort = { field: 'id', sort: 'asc' };

  // Initial filter values
  const initialFilters = { DescriptionSearchType: 'C', DerivativeCodeSearchType: 'C' };

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="data-type"
                    id={params.row.id}
                    endpoint="/vehicle"
                    deleteApiEndpoint="/trims"
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            {
              field: 'id',
              headerName: 'ID',
              hide: true
            },
            { field: 'status', headerName: 'Status' },
            { field: 'derivativeCode', headerName: 'Derivative Code' },
            {
              field: 'msrp',
              headerName: 'MSRP',
              valueFormatter: function (params) {
                return formatCurrency(params.value);
              }
            },
            {
              field: 'retailerNet',
              headerName: 'Retailer Net',
              valueFormatter: function (params) {
                return formatCurrency(params.value);
              }
            },
            { field: 'country', headerName: 'Country' },
            { field: 'year', headerName: 'Year' },
            { field: 'model', headerName: 'Model' },
            { field: 'description', headerName: 'Description' },
            { field: 'engine', headerName: 'Engine' },
            { field: 'salesVersion', headerName: 'Sales Version' },
            { field: 'gearbox', headerName: 'Transmission' },
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          let text = row[id];

          // Add extra chars that might appear on the price
          if (id === 'msrp' || id === 'retailerNet') {
            text += '$,.00';
          }

          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: row.trimId,
        country: row.countryName,
        year: row.yearDescription,
        derivativeCode: row.trimCode,
        msrp: row.trimPrice,
        retailerNet: row.trimRetailerNet,
        status: row.statusDescription
      };

      // Object values
      const objects = {
        model: row.modelDescription,
        engine: row.engineDescription,
        salesVersion: row.salesVersionDescription,
        gearbox: row.gearboxDescription,
        description: row.trimDescription,
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    const engineCore_ = engineCore();
    const engineFilter_ = engineFilter();

    const salesVersionCore_ = salesVersionCore();
    const salesVersionFilter_ = salesVersionFilter();

    const gearboxCore_ = gearboxCore();
    const gearboxFilter_ = gearboxFilter();

    const statusCore_ = statusCore();
    const statusFilter_ = statusFilter();

    return [
      [
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={countryFilter_.parameter}
              label="Country"
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={yearFilter_.parameter}
              label="Year"
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={modelFilter_.parameter}
              label="Model"
              endpoint={modelCore_.endpointSimple}
              getRows={modelFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={engineFilter_.parameter}
              label="Engine"
              endpoint={engineCore_.endpointSimple}
              getRows={engineFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={salesVersionFilter_.parameter}
              label="Sales Version"
              endpoint={salesVersionCore_.endpointSimple}
              getRows={salesVersionFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={gearboxFilter_.parameter}
              label="Transmission"
              endpoint={gearboxCore_.endpointSimple}
              getRows={gearboxFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="select"
              parameter={statusFilter_.parameter}
              label="Status"
              endpoint={statusCore_.endpoint}
              getRows={statusFilter_.getRows}
            />
          )
        }
      ],
      [
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['DerivativeCodeSearchType', 'DerivativeCode']}
              label="Derivative Code"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['DescriptionSearchType', 'Description']}
              label="Description"
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters
  };
}

// Trim detail
export function trimDetail() {
  const baseUrl = '/vehicle';

  const watchedParameters = {
    modelId: null,
    engineId: null,
    salesVersionId: null,
    gearboxId: null,
    steeringId: null,
    countryId: null
  };

  const generateDerivativeCode = (
    modelId,
    engineId,
    salesVersionId,
    gearboxId,
    steeringId,
    countryId
  ) => {
    if (
      modelId &&
      modelId.code &&
      engineId &&
      engineId.code &&
      salesVersionId &&
      salesVersionId.code &&
      gearboxId &&
      gearboxId.code &&
      steeringId &&
      steeringId.value &&
      countryId &&
      countryId.code
    ) {
      const bodyCode = '0';
      return `${modelId.code}${engineId.code}${salesVersionId.code}${bodyCode}${gearboxId.code}${steeringId.value}${countryId.code}`;
    } else {
      return '';
    }
  };

  const generateFullDescription = (
    modelId,
    engineId,
    salesVersionId,
    gearboxId
  ) => {
    if (
      modelId &&
      modelId.label &&
      engineId &&
      engineId.label &&
      salesVersionId &&
      salesVersionId.label &&
      gearboxId &&
      gearboxId.label
    ) {
      return `${modelId.label} ${engineId.label} ${gearboxId.label} ${salesVersionId.label}`;
    } else {
      return '';
    }
  };

  const getInputs = (data = null) => {
    // set an object of all the field values
    // where the default state is empty for each
    let detailObj = {
      trimId: '',
      salesVersionId: '',
      countryId: '',
      modelId: '',
      yearId: '',
      engineId: '',
      gearboxId: '',
      steeringId: '',
      trimDescription: '',
      trimDescriptionText: '',
      trimCode: '',
      trimPrice: 0,
      trimRetailerNet: 0,
      trimEnabled: false,
      priceMajorVersion: 0,
      priceMinorVersion: 0
    };

    if (data !== null) {

      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj).forEach(([i, prop]) => {
        detailObj[i] = data[i];
      });

      // Get the text/value from the default culture
      detailObj.trimDescription.translations.forEach((culture, k) => {
        if (culture.defaultCulture === true) {
          detailObj.trimDescriptionText = culture.text;
        }
      });
    }

    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();
    const modelDetail_ = modelDetail();

    const engineCore_ = engineCore();
    const engineFilter_ = engineFilter();
    const engineDetail_ = engineDetail();

    const salesVersionCore_ = salesVersionCore();
    const salesVersionFilter_ = salesVersionFilter();
    const salesVersionDetail_ = salesVersionDetail();

    const gearboxCore_ = gearboxCore();
    const gearboxFilter_ = gearboxFilter();
    const gearboxDetail_ = gearboxDetail();

    const trimCore_ = trimCore();

    return [
      // Data Version (description.MajorVersion + description.MinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "data-type",
                versionType: "description",
                versionLabel: "Data Version",
                endPoint: trimCore_.endpointDescriptionVersion,
                itemId: detailObj.trimId,
                majorVersion: detailObj.trimDescription.majorVersion,
                minorVersion: detailObj.trimDescription.minorVersion,
                dialogueTitle: "Data Version",
                ctaLabel: "Edit Data version"
              }}
            />
          )
        }
      ],
      // Price Version (priceMajorVersion + priceMinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "data-type",
                versionType: "price",
                versionLabel: "Price Version",
                endPoint: trimCore_.endpointPriceVersion,
                itemId: detailObj.trimId,
                majorVersion: detailObj.priceMajorVersion,
                minorVersion: detailObj.priceMinorVersion,
                dialogueTitle: "Price Version",
                ctaLabel: "Edit Price version"
              }}
            />
          )
        }
      ],
      // Country
      [
        {
          colSize: { xs: 12, md: 10, lg: 8 },
          size: { xs: 8, md: 9, xl: 6, mxl: 4 },
          component: (
            <FormInput
              type="select"
              parameter={countryCore_.detailId}
              label="Country"
              required={true}
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
              inputValue={detailObj.countryId}
              isCulturesSource={true}
            />
          )
        }
      ],
      // Year
      [
        {
          colSize: { xs: 12, md: 10, lg: 8 },
          size: { xs: 8, md: 9, xl: 6, mxl: 4 },
          component: (
            <FormInput
              type="select"
              taxonomyName="year"
              parameter={yearCore_.detailId}
              label="Year"
              required={true}
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
              inputValue={detailObj.yearId}
            />
          )
        }
      ],
      // Model
      [
        {
          colSize: { xs: 12, md: 10, lg: 8 },
          size: { xs: 8, md: 9, xl: 6, mxl: 4 },
          component: (
            <FormInput
              type="select"
              taxonomyName="model"
              parameter={modelCore_.detailId}
              label="Model"
              required={true}
              disabled={true}
              endpoint={modelCore_.endpoint}
              getRows={modelFilter_.getRows}
              inputValue={detailObj.modelId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          ),
          addNew: (
            <AddNew
              endpoint={modelCore_.endpoint}
              taxonomyName="model"
              inputParameter={modelCore_.detailId}
              title="Model"
              label="+ Add New"
              getInputs={modelDetail_.getInputs}
              getRows={modelFilter_.getRows}
              size={{ xs: 4, md: 3, xl: 6 }}
              disabled={true}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Engine
      [
        {
          colSize: { xs: 12, md: 10, lg: 8 },
          size: { xs: 8, md: 9, xl: 6, mxl: 4 },
          component: (
            <FormInput
              type="select"
              taxonomyName="engine"
              parameter={engineCore_.detailId}
              label="Engine"
              required={true}
              disabled={true}
              endpoint={engineCore_.endpoint}
              getRows={engineFilter_.getRows}
              inputValue={detailObj.engineId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          ),
          addNew: (
            <AddNew
              endpoint={engineCore_.endpoint}
              taxonomyName="engine"
              inputParameter={engineCore_.detailId}
              title="Engine"
              label="+ Add New"
              getInputs={engineDetail_.getInputs}
              getRows={engineFilter_.getRows}
              size={{ xs: 4, md: 3, xl: 6 }}
              disabled={true}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Sales Version
      [
        {
          colSize: { xs: 12, md: 10, lg: 8 },
          size: { xs: 8, md: 9, xl: 6, mxl: 4 },
          component: (
            <FormInput
              type="select"
              taxonomyName="salesVersion"
              parameter={salesVersionCore_.detailId}
              label="Sales Version"
              required={true}
              disabled={true}
              endpoint={salesVersionCore_.endpoint}
              getRows={salesVersionFilter_.getRows}
              inputValue={detailObj.salesVersionId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          ),
          addNew: (
            <AddNew
              endpoint={salesVersionCore_.endpoint}
              taxonomyName="salesVersion"
              inputParameter={salesVersionCore_.detailId}
              title="Sales Version"
              label="+ Add New"
              getInputs={salesVersionDetail_.getInputs}
              getRows={salesVersionFilter_.getRows}
              size={{ xs: 4, md: 3, xl: 6 }}
              disabled={true}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Gearbox
      [
        {
          colSize: { xs: 12, md: 10, lg: 8 },
          size: { xs: 8, md: 9, xl: 6, mxl: 4 },
          component: (
            <FormInput
              type="select"
              taxonomyName="gearbox"
              parameter={gearboxCore_.detailId}
              label="Transmission"
              required={true}
              disabled={true}
              endpoint={gearboxCore_.endpoint}
              getRows={gearboxFilter_.getRows}
              inputValue={detailObj.gearboxId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          ),
          addNew: (
            <AddNew
              endpoint={gearboxCore_.endpoint}
              taxonomyName="gearbox"
              inputParameter={gearboxCore_.detailId}
              title="Transmission"
              label="+ Add New"
              getInputs={gearboxDetail_.getInputs}
              getRows={gearboxFilter_.getRows}
              size={{ xs: 4, md: 3, xl: 6 }}
              disabled={true}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Steering
      [
        {
          hidden: true,
          component: (
            // Hardcoded as requested (PM/BE), sorry... 1 = "left hand"
            <FormInput type="hidden" parameter="steeringId" inputValue="1" />
          )
        }
      ],
      // Description
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="localization"
              parameter="trimDescription"
              label="Description"
              required={true}
              inputValue={{
                key: detailObj.trimDescription.key
                  ? detailObj.trimDescription.key
                  : '',
                translations: detailObj.trimDescription.translations
                  ? detailObj.trimDescription.translations
                  : [],
                defaultCultureText: detailObj.trimDescriptionText
              }}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
              generatedValueKey="fullDescription"
            />
          )
        }
      ],
      // Trim Code
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="trimDerivativeCode"
              label="Derivative Code"
              required={true}
              inputValue={detailObj.trimCode}
              inputProps={{ pattern: '[0-9A-Z]{1,15}', maxLength: 15 }}
              generatedValueKey="derivativeCode"
            />
          )
        }
      ],
      // MSRP
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="price"
              priceType="price"
              label="MSRP Price"
              parameter="trimPrice"
              required={true}
              inputValue={detailObj.trimPrice}
            />
          )
        }
      ],
      // Retailer Net
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="price"
              priceType="retailerNet"
              label="Retailer Net"
              parameter="trimRetailerNet"
              required={false}
              inputValue={detailObj.trimRetailerNet}
            />
          )
        }
      ],
      // Enabled
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="checkbox"
              label="Enabled"
              parameter="trimEnabled"
              required={true}
              inputValue={detailObj.trimEnabled}
            />
          )
        }
      ]
    ];
  };

  return {
    baseUrl,
    getInputs: getInputs,
    watchedParameters: watchedParameters,
    generateDerivativeCode: generateDerivativeCode,
    generateFullDescription: generateFullDescription
  };
}
